import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Instagram, Facebook, Youtube } from 'lucide-react';
import PaymentMethodsRow from './PaymentMethodsRow';

const Footer = () => {
  const navigate = useNavigate();

  const handleNavigate = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const exploreLinks = [
    { label: 'Home', path: '/' },
    { label: 'Our Story', path: '/our-story' },
    { label: 'Treatments', path: '/facials' },
    { label: 'Shop', path: '/shop' },
    { label: 'Gift Cards', path: '/gift-cards' },
    { label: 'Blog', path: '/blog' },
    { label: 'Membership', path: '/membership' },
    { label: 'Contact', path: '/contact' },
  ];

  const usefulLinks = [
    { label: 'Areas We Service', path: '/areas-we-service' },
    { label: 'FAQ', path: '/faq' },
    { label: 'Aftercare', path: '/aftercare' },
    { label: 'Beauty Trends', path: '/beauty-trends' },
    { label: 'Reviews', path: '/testimonials' },
    { label: 'Shipping & Returns', path: '/shipping-returns' },
    { label: 'Murad Clinical Statements', path: '/murad-clinical-statements' },
    { label: 'Policies', path: '/policies' },
    { label: 'Terms & Conditions', path: '/terms' },
  ];

  const socialIconClasses =
    'w-9 h-9 rounded-full flex items-center justify-center bg-beauty-black/[0.04] hover:bg-beauty-green hover:text-white text-beauty-black transition-all';

  return (
    <footer className="bg-beauty-white border-t border-beauty-muted/20 pt-16 pb-10" data-testid="site-footer">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-5xl mx-auto">
          {/* Brand Column */}
          <div>
            <h3 className="text-lg font-serif font-bold text-beauty-black mb-3">
              AT THE BEAUTY BAR
            </h3>
            <p className="text-beauty-green italic text-sm mb-4">
              glow up. chill out. shine on.
            </p>
            <p className="text-beauty-text text-sm leading-relaxed">
              Private women-only facial studio
              <br />
              25 Teak Street, Caulfield South
              <br />
              Melbourne, Australia
            </p>
            <div className="mt-3" data-testid="footer-hours">
              <p className="text-[10px] uppercase tracking-[0.2em] text-beauty-green/90 font-semibold">
                Studio Hours
              </p>
              <p className="text-beauty-text text-xs mt-1 leading-relaxed">
                Tue – Thu · 10:30am – 4:30pm
                <br />
                <span className="text-beauty-text/85">By appointment only</span>
              </p>
            </div>
            <div className="mt-4 space-y-1">
              <p className="text-beauty-text text-xs">
                WhatsApp / SMS:{' '}
                <a href="sms:0423212612" className="hover:text-beauty-green transition-colors">
                  0423 212 612
                </a>
              </p>
              <p className="text-beauty-text text-xs">
                Studio Line:{' '}
                <a href="tel:1300456987" className="hover:text-beauty-green transition-colors">
                  1300 GLOW UP
                </a>
              </p>
              <p className="text-beauty-text text-xs">
                <a href="mailto:info@atthebeautybar.com.au" className="hover:text-beauty-green transition-colors">
                  info@atthebeautybar.com.au
                </a>
              </p>
            </div>

            {/* Social media */}
            <div className="mt-6">
              <p className="text-[10px] uppercase tracking-[0.2em] text-beauty-text/85 mb-3 font-semibold">
                Follow us
              </p>
              <div className="flex items-center gap-3" data-testid="footer-social-links">
                <a
                  href="https://www.instagram.com/atthebeautybar/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="AT THE BEAUTY BAR on Instagram"
                  className={socialIconClasses}
                  data-testid="footer-social-instagram"
                >
                  <Instagram className="w-4 h-4" />
                </a>
                <a
                  href="https://www.facebook.com/atthebeautybar"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="AT THE BEAUTY BAR on Facebook"
                  className={socialIconClasses}
                  data-testid="footer-social-facebook"
                >
                  <Facebook className="w-4 h-4" />
                </a>
                <a
                  href="https://www.youtube.com/@atthebeautybar"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="AT THE BEAUTY BAR on YouTube"
                  className={socialIconClasses}
                  data-testid="footer-social-youtube"
                >
                  <Youtube className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>

          {/* Explore Column */}
          <div>
            <h4 className="text-xs uppercase tracking-wider text-beauty-text/85 mb-5 font-semibold">
              Explore
            </h4>
            <ul className="space-y-3">
              {exploreLinks.map((link) => (
                <li key={link.path}>
                  <button
                    onClick={() => handleNavigate(link.path)}
                    className="text-beauty-text hover:text-beauty-green transition-colors text-sm text-left"
                    data-testid={`footer-explore-${link.path.replace('/', '') || 'home'}`}
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Useful Links Column */}
          <div>
            <h4 className="text-xs uppercase tracking-wider text-beauty-text/85 mb-5 font-semibold">
              Useful Links
            </h4>
            <ul className="space-y-3">
              {usefulLinks.map((link) => (
                <li key={link.path}>
                  <button
                    onClick={() => handleNavigate(link.path)}
                    className="text-beauty-text hover:text-beauty-green transition-colors text-sm text-left"
                    data-testid={`footer-useful-${link.path.replace('/', '')}`}
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Fine Print */}
        <div className="mt-16 pt-8 border-t border-beauty-muted/20 max-w-5xl mx-auto space-y-6">
          {/* Payment methods row — informational; actual options render at Shopify checkout */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <p className="text-[10px] uppercase tracking-[0.22em] text-beauty-text/85 font-semibold">
              We accept
            </p>
            <PaymentMethodsRow />
          </div>
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 text-xs text-beauty-text/85">
            <p>© 2026 At The Beauty Bar | Holistic Face Studio</p>
            <p>Please note: AT THE BEAUTY BAR is a private, women-only facial studio.</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
